'use client';

import React, { useContext, FC, useState, useEffect, useMemo } from 'react';
import { Production, ProductionInterval } from '../_components/production';
import { useQueryOrg } from '@services/useQueryOrg';
import { WaitForLoad } from '@components/spinner/Spinner';
import { RootStoreContext } from '@stores/rootStore';
import { observer } from 'mobx-react';
import Loading from './loading';
import { useSearchParams } from 'next/navigation';
import { YieldStatsTransformed } from '@services/responseTypes';
import { DateTime } from 'luxon';
const computeDefaultRange = (interval: ProductionInterval): {
  from: string;
  to: string;
} => {
  const now = DateTime.now();
  switch (interval) {
    case 'day':
      {
        return {
          from: now.startOf('week').toISODate()!,
          to: now.endOf('week').plus({
            weeks: 1
          }).toISODate()!
        };
      }
    case 'week':
      {
        return {
          from: now.startOf('month').toISODate()!,
          to: now.endOf('month').toISODate()!
        };
      }
    case 'month':
    default:
      {
        return {
          from: now.startOf('year').toISODate()!,
          to: now.endOf('year').toISODate()!
        };
      }
  }
};
const Page: FC = observer(() => {
  const {
    uiStore
  } = useContext(RootStoreContext);
  const searchParams = useSearchParams();
  const [interval, setInterval] = useState<ProductionInterval>('month');
  const isMatrix = uiStore.currentOrganization?.hasSupplyChainFeature ?? false;
  const initialRange = useMemo(() => computeDefaultRange('month'), []);
  const [dateRange, setDateRange] = useState<{
    from: string | null;
    to: string | null;
  }>({
    from: initialRange.from,
    to: initialRange.to
  });
  const [dateRangeUserModified, setDateRangeUserModified] = useState(false);
  useEffect(() => {
    if (!isMatrix) return;
    const def = computeDefaultRange(interval);
    setDateRange({
      from: def.from,
      to: def.to
    });
    setDateRangeUserModified(false);
  }, [interval, isMatrix]);
  const onIntervalChange = (next: ProductionInterval) => setInterval(next);
  const onDateRangeChange = (from: string | null, to: string | null) => {
    setDateRange({
      from,
      to
    });
    setDateRangeUserModified(true);
  };
  const onDateRangeReset = () => {
    const def = computeDefaultRange(interval);
    setDateRange({
      from: def.from,
      to: def.to
    });
    setDateRangeUserModified(false);
  };
  const sendDateRange = isMatrix && dateRangeUserModified && !!dateRange.from && !!dateRange.to;
  const {
    data,
    isLoading
  } = useQueryOrg<YieldStatsTransformed | undefined>({
    queryKey: ['overview', 'yield stats', searchParams?.get('filter[season_id]'), searchParams?.get('filter[year]'), searchParams?.get('filter[crop]'), searchParams?.get('filter[label_ids][]'), searchParams?.get('filter[country]'), searchParams?.get('filter[province]'), searchParams?.get('filter[village]'), searchParams?.get('filter[subdistrict]'), searchParams?.get('filter[district]'), interval, sendDateRange ? dateRange.from : null, sendDateRange ? dateRange.to : null],
    queryFn: () => uiStore.currentOrganization?.fetchYieldStats({
      interval,
      ...(sendDateRange && {
        from: dateRange.from!,
        to: dateRange.to!
      }),
      ...(!sendDateRange && searchParams?.has('filter[season_id]') && {
        season: searchParams.get('filter[season_id]')!
      }),
      ...(searchParams?.has('filter[year]') && {
        year: parseInt(searchParams.get('filter[year]')!, 10)
      }),
      ...(searchParams?.has('filter[crop]') && {
        crop: searchParams.get('filter[crop]')!
      }),
      ...(searchParams?.has('filter[label_ids][]') && {
        labels: searchParams.get('filter[label_ids][]')!
      }),
      ...(searchParams?.has('filter[country]') && {
        country: searchParams.get('filter[country]')!
      }),
      ...(searchParams?.has('filter[province]') && {
        region: searchParams.get('filter[province]')!
      }),
      ...(searchParams?.has('filter[village]') && {
        village: searchParams.get('filter[village]')!
      }),
      ...(searchParams?.has('filter[subdistrict]') && {
        subdistrict: searchParams.get('filter[subdistrict]')!
      }),
      ...(searchParams?.has('filter[district]') && {
        district: searchParams.get('filter[district]')!
      })
    }),
    enabled: searchParams?.has('filter[year]') || searchParams?.has('filter[season_id]') || isMatrix && !!dateRange.from && !!dateRange.to,
    refetchOnWindowFocus: false
  });
  const dailyMin = uiStore.currentOrganization?.prodTargets?.dailyMin;
  const dailyMax = uiStore.currentOrganization?.prodTargets?.dailyMax;
  const stats = useMemo<YieldStatsTransformed | undefined>(() => {
    if (!data) return data;
    let next: YieldStatsTransformed = data;
    if (dailyMin === 0 && dailyMax === 0) {
      next = {
        ...next,
        targetMin: 0,
        targetMax: 0
      };
    }
    return next;
  }, [data, dailyMin, dailyMax]);
  return <WaitForLoad isLoading={isLoading} customSpinner={<Loading />}>
      {() => <Production stats={stats} interval={interval} onIntervalChange={onIntervalChange} dateRange={dateRange} onDateRangeChange={onDateRangeChange} onDateRangeReset={onDateRangeReset} />}
    </WaitForLoad>;
});
export default Page;